import { DateTime } from 'luxon';
import { useState } from 'react';

import { cn } from '../cn';
import { formatPhoneDisplay } from '../ContactDetail/formatPhoneDisplay';
import Button from '../Input2/Button';
import Radio from '../Input2/Radio';
import Popup from '../Popup/Popup';

/** One number this rep has actually called this contact from — see `pgDialNumberHistory`. */
export interface DialNumberHistoryEntry {
  phoneNumber: string;
  /** Epoch ms of the most recent call placed from this number. */
  lastUsedAt: number;
  callCount: number;
}

/** One of the rep's numbers against today's warm-up limit — see `voiceCall.getContactDialInfo`. */
export interface DialNumberLimit {
  phoneNumber: string;
  callsToday: number;
  cap: number;
  /** The dialer won't call from this number again until midnight. */
  atCap: boolean;
}

export interface ChangeDialNumberPopupProps {
  contactName?: string;
  /** The contact's currently pinned from-number (null when nothing is pinned yet). */
  currentNumber: string | null;
  /**
   * The pin is settled: the contact answered on it, or the rep picked it by hand. Rotation no
   * longer moves it, which is the thing `onClear` undoes.
   */
  locked?: boolean;
  /** The rep's assigned numbers (E.164) — the only values the contact can be re-pinned to. */
  bucketNumbers: string[];
  /** Which numbers this contact has actually been called from, most recently used first. */
  history?: DialNumberHistoryEntry[];
  /**
   * Each number's calls today against its daily limit, when the team limits calls per number.
   * A number at its limit can still be picked, but the next call borrows another number until
   * midnight — so the rep sees that before choosing it rather than after.
   */
  numberLimits?: DialNumberLimit[];
  saving?: boolean;
  /** Called once with the picked number when the rep confirms. */
  onConfirm: (phoneNumber: string) => void;
  /**
   * Present = offer "Use automatic rotation", which drops the association entirely. Pass it only
   * when there is a pin to clear; omit it and the affordance is hidden rather than dead.
   */
  onClear?: () => void;
  clearing?: boolean;
  onClose: () => void;
}

/**
 * The deliberate confirmation behind the dialer's "Change" affordance. Left alone, a contact's
 * from-number moves through the rep's numbers while calls go unanswered and settles for good once
 * one connects. Choosing one here overrides that and fixes the number permanently, so it's meant
 * to be a considered action (e.g. the number died) — hence a popup with explicit copy and a
 * confirm button instead of an inline dropdown.
 *
 * Each number carries what this contact has actually been called from, so "which one did we use
 * before?" is answerable at the moment of picking; and a pinned contact can be handed back to
 * rotation outright, which is otherwise a one-way door once a call connects.
 */
export function ChangeDialNumberPopup({
  contactName,
  currentNumber,
  locked,
  bucketNumbers,
  history,
  numberLimits,
  saving,
  onConfirm,
  onClear,
  clearing,
  onClose,
}: ChangeDialNumberPopupProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const canConfirm = picked != null && picked !== currentNumber;
  const busy = saving === true || clearing === true;

  const handleConfirm = () => {
    if (picked == null || picked === currentNumber) return;
    onConfirm(picked);
  };

  // Numbers this contact was called from that the rep no longer holds — they can't be picked, but
  // they're exactly what makes a surprising pin make sense ("oh, that one was reassigned").
  // The current number is never "also called from", even when it's outside the bucket: a
  // legacy/superadmin-provisioned rep has no bucket at all, and a stale pin is still the number the
  // next call goes out from.
  const pastOnly = (history ?? []).filter((h) => h.phoneNumber !== currentNumber && !bucketNumbers.includes(h.phoneNumber));

  return (
    <Popup onClose={onClose} className="w-96">
      <span className="text-base font-semibold" data-test="change-dial-number-title">
        Change calling number
      </span>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Calls to {contactName ?? 'this contact'} move to a different number each time they go unanswered, then stay on
        whichever one they answer. Picking a number here stops that and uses it for all future calls to this contact.
      </p>
      {locked && (
        <p className="text-sm text-gray-600 dark:text-gray-300" data-test="dial-number-locked-note">
          This contact is already fixed to one number, so it no longer rotates.
        </p>
      )}
      <Radio
        className="mx-auto text-left"
        name="dial-number"
        disabled={busy}
        // Value stays raw E.164 — that's what goes to the server; only the label is prettified,
        // matching the "Calling from …" caption that opens this popup.
        options={bucketNumbers.map((n) => {
          const used = history?.find((h) => h.phoneNumber === n);
          const limit = numberLimits?.find((l) => l.phoneNumber === n);
          return {
            value: n,
            label: n === currentNumber ? `${formatPhoneDisplay(n)} (current)` : formatPhoneDisplay(n),
            action: (
              <span className="flex flex-col items-end">
                <span
                  className="whitespace-nowrap text-xs text-gray-400 dark:text-gray-500"
                  data-test="dial-number-used">
                  {used == null
                    ? 'Never used'
                    : `${used.callCount} call${used.callCount === 1 ? '' : 's'} · ${DateTime.fromMillis(used.lastUsedAt).toRelative() ?? ''}`}
                </span>
                {limit != null && (
                  <span
                    className={cn(
                      'whitespace-nowrap text-xs tabular-nums',
                      limit.atCap ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500',
                    )}
                    data-test="dial-number-limit">
                    {limit.atCap
                      ? `At today’s limit (${limit.callsToday}/${limit.cap})`
                      : `${limit.callsToday}/${limit.cap} today`}
                  </span>
                )}
              </span>
            ),
          };
        })}
        value={picked ?? currentNumber ?? undefined}
        onChange={setPicked}
        data-test="dial-number-options"
      />
      {pastOnly.length > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400" data-test="dial-number-past-only">
          <p className="font-medium">Also called from (no longer yours)</p>
          {pastOnly.map((h) => (
            <p key={h.phoneNumber} className="tabular-nums">
              {formatPhoneDisplay(h.phoneNumber)} — {h.callCount} call{h.callCount === 1 ? '' : 's'} ·{' '}
              {DateTime.fromMillis(h.lastUsedAt).toRelative()}
            </p>
          ))}
        </div>
      )}
      <div className="flex items-center justify-center gap-2">
        <Button tone="outline" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
        {onClear && (
          <Button
            tone="outline"
            color="red"
            disabled={busy}
            status={clearing ? 'updating' : undefined}
            onClick={onClear}
            tooltip="Drop this number and let calls rotate again"
            data-test="clear-dial-number">
            Unset
          </Button>
        )}
        <Button
          color="blue"
          // Also disabled while saving — `status` alone doesn't block a double-submit click.
          disabled={!canConfirm || busy}
          status={saving ? 'updating' : undefined}
          onClick={handleConfirm}
          data-test="confirm-dial-number">
          Use this number
        </Button>
      </div>
    </Popup>
  );
}
